import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { smoothScroll } from 'utils/smoothScroll';
import Link from './Link';
import styles from './NavigationBar.module.scss';
import type { MouseEventHandler, MutableRefObject } from 'react';

export type NavBarItem = {
  anchorIdOrUrl: string;
  text: string;
  ref?: MutableRefObject<HTMLElement | null>;
  hideOnMobile?: boolean;
  isEmpty?: boolean;
};

export type NavBarItems = {
  byId: Record<string, NavBarItem>;
  allIds: string[];
};

type Props = {
  items: NavBarItems;
  activeItemIndex: number;
};

const NavigationBar = ({ items, activeItemIndex }: Props): JSX.Element => {
  const [headerHeight, setHeaderHeight] = useState<number>(0);
  const [navBarHeight, setNavBarHeight] = useState<number>(0);

  useEffect(() => {
    const navBar = document.querySelector('.navBar');
    const header = document.querySelector('header');
    if (!navBar || !header) {
      return;
    }

    // Using timeout to wait for the Header
    // component to finish rendering.
    setTimeout(() => {
      setNavBarHeight(navBar.clientHeight);
      setHeaderHeight(header.clientHeight);
    });

    if (typeof ResizeObserver === 'undefined') {
      // If no ResizeObserver support, header height
      // will be set only once (navbar may look displaced
      // if viewport changes).
      return;
    }

    const resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.contentRect.height === headerHeight) {
          return;
        }

        setHeaderHeight(entry.contentRect.height);
      });
    });

    resizeObserver.observe(header);

    return () => resizeObserver.disconnect();
  }, []);

  const scrollToBlock: MouseEventHandler<HTMLElement> = (event) => {
    const target = event.target as HTMLElement;
    const navItem = target.closest('a');
    if (!navItem || !navItem.hash) {
      return;
    }

    const anchor = document.getElementById(navItem.hash.substring(1));
    if (anchor) {
      event.preventDefault();
      smoothScroll(
        document.documentElement,
        anchor,
        headerHeight + navBarHeight,
      );
    }
  };

  return (
    <nav
      className={clsx(
        styles.navBar,
        'navBar bg-stone text-primary text-xs sticky z-10 overflow-x-auto',
      )}
      style={{ top: `${headerHeight}px` }}
      onClickCapture={scrollToBlock}
    >
      <ul className="flex max-w-screen-xl mx-auto px-3 md:px-4">
        {items.allIds.map((id, i) => {
          if (items.byId[id].isEmpty) {
            return null;
          }

          return (
            <li
              key={i}
              className={clsx(
                styles.navBarItem,
                items.byId[id].hideOnMobile
                  ? 'hidden sm:inline-flex'
                  : 'inline-flex',
              )}
            >
              <Link href={items.byId[id].anchorIdOrUrl}>
                <a
                  className={clsx(
                    i === activeItemIndex
                      ? styles.active
                      : 'hover:bg-dark-stone',
                    styles.disableHoverMobile,
                    'text-siam inline-flex items-center tracking-widest py-3 px-3 uppercase transition-colors ease-in-out duration-300 sm:px-4',
                  )}
                  rel="noopener"
                >
                  {items.byId[id].text}
                </a>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default NavigationBar;
